import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { ArrowUpRight } from 'lucide-react';
import PageHeader from '@/components/common/PageHeader';
import Reveal from '@/components/common/Reveal';
import { buttonVariants, Button } from '@/components/ui/button';
import { CONTACT_INFO } from '@/constants/contact';

const credits = [
  { yr: '2022', ev: 'TechsFlow founded in Amravati, two-person studio.' },
  { yr: '2023', ev: 'First clinic website redesign for a Nagpur-based skin practice.' },
  { yr: '2024', ev: 'Shipped 11 clinic sites; started publishing case notes.' },
  { yr: '2025', ev: 'Aurederma Institute; healthcare specialisation formalised.' },
];

export default function AboutPage() {
  return (
    <motion.main
      id="main-content"
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
    >
      <Helmet>
        <title>About | TechsFlow</title>
      </Helmet>
      <PageHeader
        eyebrow="Index · 05 · About"
        title="A small studio,"
        italicTail="quietly obsessed with clinics."
        lede="TechsFlow is two designers-who-code and one long list of clinic websites. We're based in Pune, we take on eight to twelve projects a year, and we still write every email ourselves."
      />

      <section className="py-16 md:py-24">
        <div className="mx-auto container-editorial grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          <Reveal className="lg:col-span-7">
            <div className="space-y-6 text-base md:text-lg text-foreground/80 leading-relaxed max-w-2xl">
              <p className="first-letter:font-serif first-letter:text-6xl first-letter:float-left first-letter:mr-3 first-letter:leading-[0.9] first-letter:text-foreground">
                We started TechsFlow because most clinic websites read like
                brochures written by strangers. The doctor never sounds like the
                doctor. The practice never looks like the practice. Something in
                between the two, the specific tone of a good clinic was
                missing from the internet.
              </p>
              <p>
                So we made that our brief. Not tech. Not agency growth. The
                sentence-level, spacing-level, image-level work of getting a
                clinic's real voice onto a screen. We turned it into a studio,
                gave it a name, and quietly kept going.
              </p>
              <p>
                Today, most of our work is in Indian healthcare dermatology,
                dental, aesthetic and general practice with a shelf of
                hospitality, nonprofit and event sites we take on when the brief
                asks for the same care. We work in React, we design in Figma,
                we write in Notion, and we ship in weeks, not quarters.
              </p>
              <p>
                If you're reading this, thank you. You've reached the end of the
                paragraph most agency &lsquo;About&rsquo; pages don't write.
              </p>
            </div>
          </Reveal>

          <div className="lg:col-span-5 space-y-5">
            {/*<Reveal>*/}
            {/*  <div className="card-editorial p-7">*/}
            {/*    <div className="label-eyebrow mb-4">The studio, in numbers</div>*/}
            {/*    <div className="grid grid-cols-2 gap-5">*/}
            {/*      <div>*/}
            {/*        <div className="font-serif text-5xl leading-none">17</div>*/}
            {/*        <div className="text-xs text-muted-foreground mt-2">Live clinic sites</div>*/}
            {/*      </div>*/}
            {/*      <div>*/}
            {/*        <div className="font-serif text-5xl leading-none">04</div>*/}
            {/*        <div className="text-xs text-muted-foreground mt-2">Years shipping</div>*/}
            {/*      </div>*/}
            {/*      <div>*/}
            {/*        <div className="font-serif text-5xl leading-none">02</div>*/}
            {/*        <div className="text-xs text-muted-foreground mt-2">People, no juniors</div>*/}
            {/*      </div>*/}
            {/*      <div>*/}
            {/*        <div className="font-serif text-5xl leading-none">00</div>*/}
            {/*        <div className="text-xs text-muted-foreground mt-2">Templates used</div>*/}
            {/*      </div>*/}
            {/*    </div>*/}
            {/*  </div>*/}
            {/*</Reveal>*/}
            <Reveal delay={0.1}>
              <div className="card-editorial p-7">
                <div className="label-eyebrow mb-4">Timeline</div>
                <ul className="divide-y divide-border">
                  {credits.map(c => (
                    <li key={c.yr} className="flex items-baseline justify-between gap-6 py-3.5">
                      <span className="font-mono text-sm text-muted-foreground rule-numeric">{c.yr}</span>
                      <span className="text-sm md:text-base text-right flex-1">{c.ev}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Quote */}
      <section className="py-20 md:py-28 border-y border-border bg-secondary/40">
        <div className="mx-auto container-editorial">
          <Reveal>
            <p className="font-serif text-3xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight max-w-5xl text-balance">
              <span className="text-muted-foreground">&ldquo;</span>
              We treat a clinic website the way a clinic treats a first-visit
              patient: <span className="italic-serif">respectfully, unhurriedly, and with the room to ask more later.</span>
              <span className="text-muted-foreground">&rdquo;</span>
            </p>
            <div className="mt-8 flex items-center gap-4">
              <div className="w-10 h-10 rounded-full bg-foreground/90" />
              <div>
                <div className="font-medium">The TechsFlow studio</div>
                <div className="text-xs text-muted-foreground font-mono uppercase tracking-widest">A house note, kept above the desk</div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="py-20 md:py-28">
        <div className="mx-auto container-editorial flex flex-col md:flex-row items-start md:items-end justify-between gap-8">
          <Reveal>
            <div className="label-eyebrow mb-4">Now hiring quietly</div>
            <h3 className="font-serif text-3xl md:text-5xl tracking-tight max-w-2xl leading-[1] text-balance">
              Looking for a third pair of hands who cares more about kerning than titles.
            </h3>
          </Reveal>

          <Reveal delay={0.1}>
            <div className="max-w-sm">
              <div className="label-eyebrow mb-3">Write to us</div>
              <a
                  href={`mailto:${CONTACT_INFO.email}`}
                  className="text-lg md:text-xl text-sienna hover:underline transition-all"
              >
                {CONTACT_INFO.email}
              </a>
            </div>
          </Reveal>
        </div>
      </section>
    </motion.main>
  );
}
